import React, { useState } from 'react';
import { authService } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png'; // ✅ Add your logo in /src/assets/logo.png

const Register: React.FC = () => {
  const [step, setStep] = useState<'email' | 'verify' | 'register'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  /** STEP 1: Send verification code **/
  const sendCode = async () => {
    const errors: Record<string, string> = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Invalid email address';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setError(null);
      setMessage(null);
      await authService.sendVerificationCode(email);
      setMessage('Verification code sent to your email.');
      setStep('verify');
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to send code. Please try again.';

      if (
        backendMessage.toLowerCase().includes('already registered') ||
        backendMessage.toLowerCase().includes('log in')
      ) {
        setIsRegistered(true);
        setError('This email is already registered. Please log in or reset your password.');
      } else {
        setError(backendMessage);
      }
    }
  };

  /** STEP 2: Verify code **/
  const verifyCode = async () => {
    const errors: Record<string, string> = {};
    if (!code) errors.code = 'Verification code is required';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await authService.verifyCode(email, code);
      setMessage('Email verified successfully!');
      setError(null);
      setStep('register');
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.message || 'Invalid or expired code.';
      setError(backendMessage);
    }
  };

  /** STEP 3: Register user **/
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};

    if (!name) errors.name = 'Name is required';
    if (!password) errors.password = 'Password is required';
    else if (password.length < 6)
      errors.password = 'Password must be at least 6 characters';
    if (!confirmPassword) errors.confirmPassword = 'Confirm your password';
    else if (password !== confirmPassword)
      errors.confirmPassword = 'Passwords do not match';

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await register(email, password, name);
      alert('Account created successfully!');
      navigate('/');
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.message || 'Registration failed. Please try again.';
      setError(backendMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 px-4 py-12">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg space-y-6">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img src={logo} alt="App Logo" className="h-14 w-auto object-contain" />
        </div>

        {/* Title */}
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {step === 'email'
              ? 'Verify Your Email'
              : step === 'verify'
              ? 'Enter Verification Code'
              : 'Create Your Account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </p>
        </div>

        {/* Success Message */}
        {message && (
          <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-md text-sm">
            {message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded-md text-sm">
            {error}
            {isRegistered && (
              <div className="mt-2 text-sm text-gray-700">
                This email is already registered.{' '}
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Go to login
                </Link>{' '}
                and use the “Forgot Password” option if needed.
              </div>
            )}
          </div>
        )}

        {/* STEP 1: EMAIL */}
        {step === 'email' && (
          <div className="space-y-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              type="email"
              className={`block w-full px-3 py-2 border ${
                validationErrors.email ? 'border-red-500' : 'border-gray-300'
              } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setValidationErrors({});
                setError(null);
                setIsRegistered(false);
              }}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
            <button
              onClick={sendCode}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </div>
        )}

        {/* STEP 2: VERIFY CODE */}
        {step === 'verify' && (
          <div className="space-y-4">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              maxLength={6}
              className={`block w-full px-3 py-2 border ${
                validationErrors.code ? 'border-red-500' : 'border-gray-300'
              } rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm`}
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setValidationErrors({});
                setError(null);
              }}
            />
            {validationErrors.code && (
              <p className="text-sm text-red-600">{validationErrors.code}</p>
            )}
            <div className="flex justify-between space-x-2">
              <button
                onClick={() => setStep('email')}
                className="w-1/2 py-2 px-4 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Back
              </button>
              <button
                onClick={verifyCode}
                disabled={loading}
                className="w-1/2 py-2 px-4 border border-transparent rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: REGISTER */}
        {step === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                className={`block w-full px-3 py-2 border ${
                  validationErrors.name ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {validationErrors.name && (
                <p className="text-sm text-red-600">{validationErrors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                className={`block w-full px-3 py-2 border ${
                  validationErrors.password ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {validationErrors.password && (
                <p className="text-sm text-red-600">{validationErrors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                className={`block w-full px-3 py-2 border ${
                  validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {validationErrors.confirmPassword && (
                <p className="text-sm text-red-600">{validationErrors.confirmPassword}</p>
              )}
            </div>

            <div className="flex justify-between space-x-2">
              <button
                type="button"
                onClick={() => setStep('verify')}
                className="w-1/2 py-2 px-4 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-1/2 py-2 px-4 border border-transparent rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Register'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register;
